import { RJSFSchema, UiSchema } from "@rjsf/utils";
import {
  CustomFormSchemaRecord,
  customFormSchemaFromJSON,
  customFormSchemaToJSON,
  ICustomFormSchemaData,
} from "common/records/CustomFormSchema";
import { List, Map, Record } from "immutable";

export const jobResultingOptionFromJSON = (json: Partial<IJobResultingOptionData>): JobResultingOptionRecord => {
  const record: IJobResultingOption = { ...(json as any) };

  // This is currently the only allowed value
  record.default_type = "email";

  const uiOrder = List(json?.uiSchema?.["ui:order"] || []);
  record.schema = customFormSchemaFromJSON(json?.schema || {}, uiOrder);

  if (json.uiSchema) {
    record.uiSchema = Map(json.uiSchema);
  }

  return new JobResultingOptionRecord(record);
};

export const jobResultingOptionToJSON = (option: JobResultingOptionRecord): Partial<IJobResultingOptionData> => {
  const json = option.toJS() as Partial<IJobResultingOptionData>;

  const uiOrder: string[] = [];

  if (option.schema) {
    json.schema = customFormSchemaToJSON(option.schema);

    if (json.schema?.properties) {
      for (const key of Object.keys(json.schema.properties)) {
        const fieldName = json.schema.properties[key].fieldName;
        if (!fieldName) {
          continue;
        }

        uiOrder.push(fieldName);
      }
    }
  }

  if (json.uiSchema) {
    json.uiSchema["ui:order"] = uiOrder;
  }

  return json;
};

export const extractRjsfSchema = (record: JobResultingOptionRecord): RJSFSchema => {
  const json = customFormSchemaToJSON(record.schema);

  const rjsfSchema: RJSFSchema = {};

  const requiredFields: string[] = [];
  for (const schemaKey of Object.keys(json)) {
    if (schemaKey === "properties") {
      const properties = json.properties || {};
      const rjsfProperties: NonNullable<RJSFSchema["properties"]> = {};

      for (const propertyKey of Object.keys(properties)) {
        // properties[propertyKey] is ICustomFormSchemaFieldsData — OCC keys are known here
        const { fieldName, occ_type, required, ...rjsfField } = properties[propertyKey];
        rjsfProperties[propertyKey] = rjsfField;

        if (required && typeof fieldName === "string") {
          requiredFields.push(fieldName);
        }
      }

      rjsfSchema.properties = rjsfProperties;
      continue; // already handled; skip the line-78 copy for "properties"
    }

    rjsfSchema[schemaKey] = json[schemaKey];
  }

  if (rjsfSchema.properties) {
    rjsfSchema.properties.result = { type: "string" };
  }

  if (requiredFields.length) {
    rjsfSchema.required = requiredFields;
  }

  return rjsfSchema;
};

export const extractRjsfUISchema = (record: JobResultingOptionRecord): UiSchema => {
  const json: UiSchema = { ...record.uiSchema.toJS() };

  json.result = { "ui:widget": "hidden" };

  if (!json["ui:order"]?.includes("result")) {
    json["ui:order"]?.push("result");
  }

  return json;
};

export interface IJobResultingOptionData {
  backup_email: string;
  bcc_email: string;
  cc_email: string;
  default_type: string;
  email_subject: string;
  name: string;
  pattern_id: number;
  schema: ICustomFormSchemaData;
  to_email: string;
  uiSchema: UiSchema;
}

export interface IJobResultingOption {
  backup_email: string;
  bcc_email: string;
  cc_email: string;
  default_type: "email";
  email_subject: string;
  name: string;
  pattern_id: number;
  schema: CustomFormSchemaRecord;
  to_email: string;
  uiSchema: Map<keyof UiSchema, UiSchema[keyof UiSchema]>;
}

export const defaultJobResultOptionProps: IJobResultingOption = {
  backup_email: "",
  bcc_email: "",
  cc_email: "",
  default_type: "email",
  email_subject: "",
  name: "",
  pattern_id: 0,
  schema: customFormSchemaFromJSON({}),
  to_email: "",
  uiSchema: Map(),
};

export class JobResultingOptionRecord
  extends Record<IJobResultingOption>(defaultJobResultOptionProps)
  implements IJobResultingOption {}
